import type {
  CarouselAspect,
  CarouselProps,
  CarouselSlide,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import {
  CheckboxField,
  ColorField,
  NumberField,
  SelectField,
  TextField,
} from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const ASPECT = ["16/9", "4/3", "1/1", "21/9"] as const;

function newId(): string {
  return `cs-${Math.random().toString(36).slice(2, 8)}`;
}

export function CarouselInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as CarouselProps;
  const slides = p.slides ?? [];

  const setSlides = (next: CarouselSlide[]) => update(widget.id, { slides: next });
  const addSlide = () =>
    setSlides([
      ...slides,
      {
        id: newId(),
        imageUrl: "https://placehold.co/1600x900/111827/ffffff?text=Slide",
        alt: "Slide",
      },
    ]);
  const updateSlide = (id: string, patch: Partial<CarouselSlide>) =>
    setSlides(slides.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeSlide = (id: string) => setSlides(slides.filter((s) => s.id !== id));
  const moveSlide = (id: string, dir: "up" | "down") => {
    const idx = slides.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= slides.length) return;
    const next = slides.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setSlides(next);
  };

  return (
    <>
      <SelectField
        label="Aspect ratio"
        value={p.aspectRatio ?? "16/9"}
        options={ASPECT}
        onChange={(v) => update(widget.id, { aspectRatio: v as CarouselAspect })}
      />
      <CheckboxField
        label="Show dots"
        value={p.showDots ?? true}
        onChange={(v) => update(widget.id, { showDots: v })}
      />
      <CheckboxField
        label="Show arrows"
        value={p.showArrows ?? true}
        onChange={(v) => update(widget.id, { showArrows: v })}
      />
      <NumberField
        label="Radius (px)"
        value={p.radius ?? 12}
        min={0}
        max={32}
        onChange={(v) => update(widget.id, { radius: v })}
      />
      <TextField
        label="Overlay colour"
        value={p.overlayColor ?? "rgba(0,0,0,0.35)"}
        onChange={(v) => update(widget.id, { overlayColor: v })}
      />
      <ColorField
        label="Caption text"
        value={p.textColor ?? "#ffffff"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Active dot accent"
        value={p.accent ?? "#ffffff"}
        onChange={(v) => update(widget.id, { accent: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Slides ({slides.length})
      </div>

      {slides.map((s, i) => (
        <div
          key={s.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveSlide(s.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveSlide(s.id, "down")}
              disabled={i === slides.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === slides.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeSlide(s.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <MediaField
            label={`Slide ${i + 1} image URL`}
            value={s.imageUrl ?? ""}
            onChange={(v) => updateSlide(s.id, { imageUrl: v || undefined })}
          />
          <TextField
            label="Alt text"
            value={s.alt ?? ""}
            onChange={(v) => updateSlide(s.id, { alt: v || undefined })}
          />
          <TextField
            label="Title (optional)"
            value={s.title ?? ""}
            onChange={(v) => updateSlide(s.id, { title: v || undefined })}
          />
          <TextField
            label="Caption (optional)"
            value={s.caption ?? ""}
            onChange={(v) => updateSlide(s.id, { caption: v || undefined })}
          />
          <TextField
            label="Link URL (optional)"
            value={s.href ?? ""}
            onChange={(v) => updateSlide(s.id, { href: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addSlide}
          style={{ width: "100%" }}
        >
          + Add slide
        </button>
      </div>
    </>
  );
}
